import React from "react";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
} from "react-router-dom";
import CssBaseline from "@mui/material/CssBaseline";
import MainLayout from "./layouts/MainLayout";
import LoadingSpinner from "./components/LoadingSpinner";
import { useAppSelector } from "./store/hooks";

import ScrollToTop from "./components/ScrollToTop";
import AutoLogout from "./components/AutoLogout";

const Login = React.lazy(() => import("./pages/Login"));
const Signup = React.lazy(() => import("./pages/Signup"));
const ForgotPassword = React.lazy(() => import("./pages/ForgotPassword"));
const SetPassword = React.lazy(() => import("./pages/SetPassword"));
const Dashboard = React.lazy(() => import("./pages/Dashboard"));
const Profile = React.lazy(() => import("./pages/Profile"));
const Users = React.lazy(() => import("./pages/Users"));
const AllScanHistory = React.lazy(
  () => import("./pages/ScanHistory/AllScanHistory"),
);
const ScanProductHistory = React.lazy(
  () => import("./pages/ScanHistory/ScanProductHistory"),
);
const CustomerDetail = React.lazy(() => import("./components/CustomerDetails"));
const ProductBatches = React.lazy(() => import("./pages/Batches/index"));

const Error = React.lazy(() => import("./pages/Error/index"));
const Customer = React.lazy(() => import("./pages/Customer/index"));
const ScanHistory = React.lazy(() => import("./pages/ScanHistory/ScanHistory"));
const AddUser = React.lazy(() => import("./pages/Users/AddUser"));
const EditUser = React.lazy(() => import("./pages/Users/EditUser"));
const Products = React.lazy(() => import("./pages/Products"));
const EditProduct = React.lazy(() => import("./pages/Products/EditProduct"));

const AddProduct = React.lazy(() => import("./pages/Products/AddProduct"));
const Settings = React.lazy(() => import("./pages/Settings"));
const Brands = React.lazy(() => import("./pages/Brands"));
const OnboardBrand = React.lazy(() => import("./pages/Brands/OnboardBrand"));
const BrandDetail = React.lazy(() => import("./pages/Brands/BrandDetail"));
const Batch = React.lazy(() => import("./pages/Batches"));
const AddBatch = React.lazy(() => import("./pages/Batches/AddBatch"));
const EditBatch = React.lazy(() => import("./pages/Batches/EditBatch"));
const ProdSealDetails = React.lazy(
  () => import("./pages/Batches/ProdSealDetails"),
);

interface ProtectedRouteProps {
  children: React.ReactNode;
}

const ProtectedRoute: React.FC<ProtectedRouteProps> = ({ children }) => {
  const { isAuthenticated, loading } = useAppSelector((state) => state.auth);
  if (loading) {
    return <LoadingSpinner message="Loading..." />;
  }
  return isAuthenticated ? <>{children}</> : <Navigate to="/login" />;
};

const SuperAdminRoute: React.FC<ProtectedRouteProps> = ({ children }) => {
  const { isAuthenticated, loading } = useAppSelector((state) => state.auth);
  const profile = useAppSelector((state) => state.users.profile);
  if (loading) {
    return <LoadingSpinner message="Loading..." />;
  }
  if (!isAuthenticated) return <Navigate to="/login" />;
  return profile?.is_superuser ? <>{children}</> : <Navigate to="/dashboard" />;
};

const App: React.FC = () => {
  return (
    <>
      <CssBaseline />
      <Router future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
        <AutoLogout>
          <React.Suspense fallback={<LoadingSpinner message="Loading..." />}>
            <ScrollToTop />
            <Routes>
              <Route path="/" element={<Login />} />
              <Route path="/login" element={<Login />} />
              <Route path="*" element={<Error />} />

              <Route path="/signup" element={<Signup />} />
              <Route path="/forgot-password" element={<ForgotPassword />} />
              <Route
                path="/set-password/:userId/:token"
                element={<SetPassword />}
              />

              
              <Route
                path="/dashboard"
                element={
                  <ProtectedRoute>
                    <MainLayout>
                      <Dashboard />
                    </MainLayout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/profile"
                element={
                  <ProtectedRoute>
                    <MainLayout>
                      <Profile />
                    </MainLayout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/users"
                element={
                  <ProtectedRoute>
                    <MainLayout>
                      <Users />
                    </MainLayout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/users/scan-history"
                element={
                  <ProtectedRoute>
                    <MainLayout>
                      <AllScanHistory />
                    </MainLayout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/prodseals/scans/:scanId"
                element={
                  <ProtectedRoute>
                    <MainLayout>
                      <ScanHistory />
                    </MainLayout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/scan-product/:productId"
                element={
                  <ProtectedRoute>
                    <MainLayout>
                      <ScanProductHistory />
                    </MainLayout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/customer"
                element={
                  <ProtectedRoute>
                    <MainLayout>
                      <Customer />
                    </MainLayout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/customer/:id"
                element={
                  <ProtectedRoute>
                    <MainLayout>
                      <CustomerDetail />
                    </MainLayout>
                  </ProtectedRoute>
                }
              />

              <Route
                path="/users/add"
                element={
                  <ProtectedRoute>
                    <MainLayout>
                      <AddUser />
                    </MainLayout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/users/edit/:userId"
                element={
                  <ProtectedRoute>
                    <MainLayout>
                      <EditUser />
                    </MainLayout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/products/:productId/batches"
                element={
                  <ProtectedRoute>
                    <MainLayout>
                      <ProductBatches />
                    </MainLayout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/products"
                element={
                  <ProtectedRoute>
                    <MainLayout>
                      <Products />
                    </MainLayout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/products/add"
                element={
                  <ProtectedRoute>
                    <MainLayout>
                      <AddProduct />
                    </MainLayout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/products/edit/:productId"
                element={
                  <ProtectedRoute>
                    <MainLayout>
                      <EditProduct />
                    </MainLayout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/batches"
                element={
                  <ProtectedRoute>
                    <MainLayout>
                      <Batch />
                    </MainLayout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/batches/add"
                element={
                  <ProtectedRoute>
                    <MainLayout>
                      <AddBatch />
                    </MainLayout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/batches/edit/:batchId"
                element={
                  <ProtectedRoute>
                    <MainLayout>
                      <EditBatch />
                    </MainLayout>
                  </ProtectedRoute>
                }
              />

              <Route
                path="/prodseals/:id"
                element={
                  <ProtectedRoute>
                    <MainLayout>
                      <ProdSealDetails />
                    </MainLayout>
                  </ProtectedRoute>
                }
              />

              <Route
                path="/settings"
                element={
                  <ProtectedRoute>
                    <MainLayout>
                      <Settings />
                    </MainLayout>
                  </ProtectedRoute>
                }
              />

              <Route
                path="/brands"
                element={
                  <SuperAdminRoute>
                    <MainLayout>
                      <Brands />
                    </MainLayout>
                  </SuperAdminRoute>
                }
              />
              <Route
                path="/brands/onboard"
                element={
                  <SuperAdminRoute>
                    <MainLayout>
                      <OnboardBrand />
                    </MainLayout>
                  </SuperAdminRoute>
                }
              />
              <Route
                path="/brands/:brandId"
                element={
                  <SuperAdminRoute>
                    <MainLayout>
                      <BrandDetail />
                    </MainLayout>
                  </SuperAdminRoute>
                }
              />
            </Routes>
          </React.Suspense>
        </AutoLogout>
      </Router>
    </>
  );
};

export default App;
